import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { Sidenavaction } from 'src/assets/js/application/sidenavaction';
import * as moment from 'moment';
import FeatureModel from '../../../core/models/feature.model';
import { FeatureService } from '../../../core/services/feature.service';
import { CommonMethodsService } from 'src/app/core/services/common-methods.service';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/core/services/previous-route.service';
import { BatchLoader } from "../../../core/utilities/batch-loader.util";
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';

declare var $: any;

/**
 * @fileOverview Feature Master Data Management Component
 * @description Administrative component for creating, updating, searching, and listing system features.
 * Implements lazy loading with infinite scroll pagination for efficient feature catalog management.
 * Used by administrators to manage available features in the system.
 * 
 * @module feature
 */

/**
 * @component FeatureComponent
 * @description Component for managing system features (master data).
 * Provides CRUD operations for feature definitions including name and status configuration.
 * Implements batch loading pagination with infinite scroll, real-time search filtering,
 * and form-based add/edit functionality.
 * 
 * Core Responsibilities:
 * - Display paginated list of system features with infinite scroll
 * - Provide form for creating new features with validation
 * - Provide form for editing existing features
 * - Implement real-time search with character normalization (spaces, hyphens removed)
 * - Format timestamps for display (DD-MMM-YYYY hh:mm:ss A)
 * - Manage form state (open/close) and double-click prevention during submission
 * - Clean up resources on component destruction
 * 
 * Features:
 * - **Lazy Loading**: Batch loader pagination with 12 items per batch (default)
 * - **Infinite Scroll**: Auto-load next batch when user scrolls near bottom of page
 * - **Real-time Search**: Filter by name or status with character normalization
 * - **Form Validation**: Required fields, length limits, pattern matching
 * - **Timestamp Formatting**: Display human-readable update timestamps
 * - **User Context**: Displays current logged-in user and business information
 * 
 * Form Fields:
 * - **name** (required): Feature name (3-20 chars, letters + optional spaces/dots)
 * - **status** (required): Feature status (e.g., ACTIVE, INACTIVE)
 * 
 * Data Flow:
 * 1. Component initialization loads all features from service
 * 2. Batch loader splits data into paginated chunks (12 per batch)
 * 3. First batch displayed; additional loaded on scroll
 * 4. Search filters features by name or status (non-paginated display)
 * 5. Form submission creates or updates feature and reloads all data
 * 
 * @selector app-feature
 * @templateUrl ./feature.component.html
 * @styleUrls ./feature.component.scss
 * 
 * @implements {OnInit} Component initialization
 * @implements {OnDestroy} Resource cleanup on destruction
 * 
 * @example
 * // In a parent routing configuration
 * {
 *   path: 'features',
 *   component: FeatureComponent,
 *   canActivate: [AuthGuard]
 * }
 */
@Component({
  selector: 'app-feature',
  templateUrl: './feature.component.html',
  styleUrls: ['./feature.component.scss']
})
export class FeatureComponent implements OnInit, OnDestroy {
  /**
   * @property {FeatureModel[]} features
   * @type {FeatureModel[]}
   * @description Complete list of all features fetched from backend (source data for batch loading)
   */
  public features: FeatureModel[] = [];

  /**
   * @property {FeatureModel[]} featuresList
   * @type {FeatureModel[]}
   * @description Currently displayed list of features (subset updated by batch loader)
   */
  public featuresList: FeatureModel[] = [];

  /**
   * @property {FeatureModel[]} filteredFeatues
   * @type {FeatureModel[]}
   * @description Filtered list matching search query (Note: typo in property name)
   */
  public filteredFeatues: FeatureModel[] = [];

  /**
   * @property {FeatureModel} feature
   * @type {FeatureModel}
   * @description Currently selected or new feature for form binding
   */
  public feature: FeatureModel = new FeatureModel();

  /**
   * @property {string} loginPersonType
   * @type {string}
   * @description Role of currently logged-in user (retrieved from localStorage)
   */
  public loginPersonType: string;

  /**
   * @property {string} loginUserName
   * @type {string}
   * @description Name of currently logged-in user (retrieved from localStorage)
   */
  public loginUserName: string;

  /**
   * @property {string} loginUserBusinessName
   * @type {string}
   * @description Business name of currently logged-in user (retrieved from localStorage)
   */
  public loginUserBusinessName: string;

  /**
   * @property {boolean} pgLoad
   * @type {boolean}
   * @description Flag indicating if page has finished initial data load
   */
  public pgLoad = false;

  /**
   * @property {boolean} submitButton
   * @type {boolean}
   * @description Flag indicating if form is in "create" mode (true) vs "update" mode (false)
   */
  public submitButton: boolean;

  /**
   * @property {boolean} updateButton
   * @type {boolean}
   * @description Flag indicating if form is in "update" mode (true) vs "create" mode (false)
   */
  public updateButton: boolean;

  /**
   * @property {string} search
   * @type {string}
   * @description Current search query string for filtering features
   */
  public search: string;

  /**
   * @property {boolean} featureDoubleClick
   * @type {boolean}
   * @description Flag to prevent double submission of form during save
   */
  public featureDoubleClick: boolean;

  /**
   * @property {boolean} NoDataMessage
   * @type {boolean}
   * @description Flag to show "No Data" message when list is empty
   */
  public NoDataMessage: boolean = false;

  /**
   * @property {number} activePageNo
   * @type {number}
   * @description Currently active page number for pagination tracking
   */
  public activePageNo: number;

  /**
   * @property {boolean} clearSearchInput
   * @type {boolean}
   * @description Flag to trigger clearing of search input field from template
   */
  public clearSearchInput: boolean = false;

  /**
   * @property {boolean} listLoader
   * @type {boolean}
   * @description Flag to show/hide batch loading spinner during pagination
   */
  public listLoader = false;

  /**
   * @property {number} startIndex
   * @type {number}
   * @description Current batch index for pagination tracking (how much has been loaded)
   */
  public startIndex = 0;

  /**
   * @property {boolean} isFiltering
   * @type {boolean}
   * @description Flag indicating if user is currently searching/filtering the list
   */
  public isFiltering: boolean = false;

  /**
   * @property {boolean} noList
   * @type {boolean}
   * @description Flag to show "No matching results" message when filtered list is empty
   */
  public noList: boolean = false;

  /**
   * @property {boolean} responseLoader
   * @type {boolean}
   * @description Flag to show/hide main page loader during initial data load
   */
  public responseLoader: boolean;
  /**
   * @property {any} scrollTimeout
   * @type {any}
   * @description Timeout ID for debouncing scroll events (50ms debounce)
   * @private
   */
  private scrollTimeout: any;

  /**
   * @property {any} searchTimeout
   * @type {any}
   * @description Timeout ID for debouncing search input events
   */
  public searchTimeout: any;

  /**
   * @property {number} formFieldCount
   * @type {number}
   * @description Number of form fields for layout control (should be 2: name, status)
   */
  public formFieldCount: number = 0;

  /**
   * @property {boolean} showForm
   * @type {boolean}
   * @description Flag to show/hide the create/edit form
   */
  public showForm: boolean = false;

  /**
   * @property {FormGroup} registerForm
   * @type {FormGroup}
   * @description Reactive form for creating and editing features
   */
  public registerForm: FormGroup;

  /**
   * @property {boolean} formSubmitted
   * @type {boolean}
   * @description Flag indicating whether form has been submitted (for showing validation errors)
   */
  public formSubmitted: boolean = false;

  /**
   * @property {BatchLoader<any>} batchLoader
   * @type {BatchLoader<any>}
   * @description Batch loader utility for handling pagination and infinite scroll
   * @private
   */
  private batchLoader: BatchLoader<any>;

  /**
   * @constructor
   * @description Initializes the FeatureComponent with dependencies and sets up reactive form
   * 
   * @param {FeatureService} featureService - Service for feature CRUD operations
   * @param {Sidenavaction} sidenavaction - Sidebar navigation actions
   * @param {CommonMethodsService} commonMethodsService - Utility service for common operations
   * @param {LocationStrategy} locationStrategy - Angular location strategy for route tracking
   * @param {PreviousRouteService} previousRouteService - Service for storing navigation history
   * @param {FormBuilder} formBuilder - Angular reactive forms builder
   * @param {ToastrService} toasterService - Notification service for user feedback
   */
  constructor(public featureService: FeatureService, public sidenavaction: Sidenavaction, public commonMethodsService: CommonMethodsService,
    private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService, public formBuilder: FormBuilder,
    public toasterService: ToastrService
  ) {
    this.feature = new FeatureModel();

    this.registerForm = this.formBuilder.group({
      name: ['',
        [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(20),
          Validators.pattern(/^(?! )[A-Za-z]+(?:[A-Za-z\s]*[.]?[A-Za-z\s]*)?$/)
        ]
      ],
      status: ['', Validators.required],
    })

    // Calculate field count after form is built
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }

  /**
   * @method ngOnInit
   * @async
   * @description Initializes the component on load. Performs:
   * 1. Scrolls page to top
   * 2. Resets all component state (lists, flags, search)
   * 3. Loads user context from localStorage (role, name, business)
   * 4. Fetches all features from service
   * 5. Formats timestamps for display
   * 6. Initializes batch loader and loads first batch
   * 
   * @public
   * @async
   * @returns {Promise<void>}
   */
  public async ngOnInit() {
    // loginperson type details
    try {
      window.scrollTo(0, 0);
      this.closeForm();
      this.features = [];
      this.featuresList = [];
      this.isFiltering = false;
      this.responseLoader = false;
      this.search = '';
      this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
      this.loginPersonType = localStorage.getItem('loginPersonType');
      this.loginUserName = localStorage.getItem('LoginUserName');
      this.loginUserBusinessName = localStorage.getItem('loginBusinessName');
      this.features = await this.featureService.getAllFeatures();
      this.pgLoad = true;
      if (this.features.length > 0) {
        this.features.forEach(eachFeature => {
          eachFeature.updatedAt = JSON.parse(JSON.stringify(moment(eachFeature.updatedAt).format('DD-MMM-YYYY hh:mm:ss A')));
        });
      }
      this.initializeBatchLoader(); // Reset instance
      this.batchLoader?.reset();     // Clear state
      await this.batchLoader?.loadNextBatch(true); // Load first batch
      this.responseLoader = true;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.responseLoader = true;
    }
  }

  /**
   * @property f
   * @getter
   * @description Getter for convenient access to form controls in template.
   * 
   * @public
   * @type {AbstractControl}
   * @returns {any} Form controls object
   */
  public get f() {
    return this.registerForm.controls;
  }

  /**
   * @method onScroll
   * @description Host listener for window scroll events. Detects when user scrolls near
   * the bottom and triggers loading of next batch (50ms debounce).
   * 
   * @public
   * @hostListener window:scroll
   * @returns {void}
   */
  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  /**
   * @method checkScrollAndLoad
   * @description Checks if user scrolled near bottom and loads next batch with 50ms debounce
   * 
   * @private
   * @returns {void}
   */
  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 && !this.batchLoader?.isLoading() && !this.isFiltering) {
        this.batchLoader.loadNextBatch();
      }
    }, 50);
  }

  /**
   * @method initializeBatchLoader
   * @description Initializes batch loader for pagination (12 items per batch)
   * 
   * @private
   * @returns {void}
   */
  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.features,
      (updatedList) => {
        this.featuresList = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined, // <-- omit or pass undefined
      (loading) => this.listLoader = loading
    );
  }

  /**
   * @method addUpdateFeature
   * @async
   * @description Creates new feature or updates existing feature. Validates form before submission,
   * prevents double-click, and reloads feature list after successful operation.
   * 
   * @public
   * @async
   * @returns {Promise<void | boolean>}
   */
  /* Add Feature */
  public async addUpdateFeature() {
    this.formSubmitted = true;
    if (this.registerForm.invalid) return false;
    try {
      this.formSubmitted = false;
      this.featureDoubleClick = true;
      if (this.submitButton) {
        this.feature = await this.featureService.create(this.feature);
        this.toasterService.success('Feature added successfully', '', {
          timeOut: 2000
        });
      } else {
        const updateFeature = Object.assign({}, this.feature);
        delete updateFeature.id;
        this.feature = await this.featureService.updateOneByID(this.feature.id, updateFeature);
        this.toasterService.success('Feature updated successfully', '', {
          timeOut: 2000
        });
      }
      this.ngOnInit();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.featureDoubleClick = false;
    }
  }

  /**
   * @method getFeatureById
   * @description Retrieves feature by ID from features list and populates reactive form with data.
   * Used for edit mode to pre-fill form with existing feature values.
   * 
   * @public
   * @param {string} id - The unique identifier of feature to retrieve
   * @returns {void}
   * @example
   * // Get feature and populate form for editing
   * this.getFeatureById('5');
   */
  /*Get feature by id*/
  public getFeatureById(id: string) {
    this.featureDoubleClick = false;
    this.updateButton = true;
    this.submitButton = false;
    this.showForm = true;
    const foundFeature = this.featuresList.find(feature => feature?.id === id);
    this.feature = foundFeature ? { ...foundFeature } : new FeatureModel(); // clone object
  }

  /**
   * @method applySearch
   * @description Filters features list based on search query. Removes hyphens, underscores and spaces
   * from both search input and feature names/status for fuzzy matching. Triggers batch reload
   * when search is cleared.
   * 
   * @public
   * @returns {void}
   * @example
   * // Search for feature containing 'user' (case-insensitive, space-agnostic)
   * this.search = 'user profile';
   * this.applySearch();
   */
  public applySearch(): void {
    this.featuresList = [];
    const query = this.search?.replace(/[-_]|\s/g, "").trim().toLowerCase();
    this.isFiltering = query.length > 0;

    if (this.isFiltering) {
      this.featuresList = this.features.filter(feature =>
        feature.name?.replace(/[-_]|\s/g, "").toLowerCase().includes(query) ||
        feature.status?.replace(/[-_]|\s/g, "").toLowerCase().includes(query)
      )
    } else {
      this.initializeBatchLoader();
      this.batchLoader?.reset();
      this.batchLoader?.loadNextBatch(true);
    }

    this.noList = this.featuresList.length === 0;
  }

  /**
   * @method openForm
   * @description Initializes create mode. Sets form state flags and displays form for new feature creation.
   * 
   * @public
   * @returns {void}
   */
  /*handle update and submit buttons*/
  public openForm() {
    this.submitButton = true;
    this.updateButton = false;
    this.showForm = true;
  }

  /**
   * @method closeForm
   * @description Closes feature form and resets all form-related state. Called on Cancel button or after successful submission.
   * 
   * @public
   * @returns {void}
   */
  /*click on cancel button form data empty reload the page*/
  public closeForm() {
    this.registerForm.reset();
    this.showForm = false;
    this.feature = new FeatureModel();
    this.submitButton = false;
    this.updateButton = false;
    this.featureDoubleClick = false;
  }

  /**
   * @method ngOnDestroy
   * @description Angular lifecycle hook. Cancels any ongoing HTTP requests, marks batch loader as destroyed,
   * and resets batch loader state to prevent memory leaks.
   * 
   * @public
   * @returns {void}
   */
  //To cancel ongoing requests and unsubscribe from subscriptions.
  public ngOnDestroy(): void {
    this.featureService.cancelOnGoingHttpRequests();
    this.batchLoader?.markAsDestroyed();
    this.batchLoader?.reset();
  }
}
